import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import { Paper, Tooltip } from '@material-ui/core';

import * as bucketSelectors from '../../selectors/bucketSelectors';
import * as bucketsConstants from '../../constants/buckets';
import * as organizationConstants from '../../constants/organizations';

import Avatar from '../Avatar';
import routes from '../../helpers/ApplicationRoutes';
import BucketImageUpload from '../bucket/BucketImageUpload';
import EditBucketModal from '../modals/EditBucketModal';
import BucketOptionsModal from '../modals/BucketOptionsModal';
import IconButton from '@material-ui/core/IconButton';

class Bucket extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            showOptionsModal: false,
            showEditModal: false,
        };
    }

    onShowBucketModal = () => {
        // TODO: Show options modal once delete is implemented.
        // this.setState({ showOptionsModal: true });showEditModal
        this.setState({ showEditModal: true });
    };

    onModalClose = () => {
        this.setState({
            showOptionsModal: false,
            showEditModal: false,
        });
    };

    onEditBucket = () => {
        this.setState({ showOptionsModal: false });
        this.setState({ showEditModal: true });
    };

    onChange = e => {
        this.setState({ checkedValue: e.target.value });
    };

    render() {
        const {
            classes,
            bucket,
            isAdmin,
            onBucketAvatarChange,
            onDeleteBucket,
            onUpdateBucket,
        } = this.props;
        const { showOptionsModal, showEditModal } = this.state;

        if (!bucket) {
            return '';
        }

        const bucketAssetsLink = routes.bucketAssets.getPath({
            bucketId: bucket.id,
            organizationId: bucket.organizationId,
            // redirect to first application
            application: bucket.visibleInApplications[0].id,
        });

        let paragraphs = [];
        let noDescription = '';

        if (bucket.description) {
            // Split by all return characters and make it a list of paragraphs.
            paragraphs = bucket.description.split(/(\r\n|\n|\r)/gm);
        } else {
            noDescription = 'No description present for this project.';
        }

        let bucketImage;
        if (isAdmin) {
            bucketImage = (
                <BucketImageUpload
                    picture={bucket.pictureLargeURL}
                    onBucketAvatarChange={onBucketAvatarChange}
                    className={classes.avatar}
                    bucket={bucket}
                />
            );
        } else {
            bucketImage = (
                <Avatar.Large
                    src={bucket.pictureLargeURL}
                    alt="profile-picture"
                    className={classes.avatar}
                />
            );
        }

        return (
            <Paper square elevation={1} className={classes.content}>
                <Paper className={classes.title}>
                    {bucketImage}
                    <Tooltip placement="bottom-start" title={bucket.name}>
                        <Link
                            className={classes.bucketLink}
                            to={bucketAssetsLink}
                        >
                            {bucket.name}
                        </Link>
                    </Tooltip>
                    {isAdmin && (
                        <Tooltip title="Edit project">
                            <IconButton
                                className={classes.bucketOptionsLink}
                                onClick={this.onShowBucketModal}
                            >
                                <img alt="menu" src="/assets/edit_icon.svg" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Paper>

                <Link to={bucketAssetsLink} className={classes.descriptionLink}>
                    <div className={classes.description}>
                        {!bucket.description
                            ? noDescription
                            : paragraphs.map((paragraph, idx) => (
                                  <p
                                      key={`bucket_paragraph_${
                                          bucket.id
                                      }_${idx}`}
                                  >
                                      {paragraph}
                                  </p>
                              ))}
                    </div>
                </Link>

                {isAdmin && (
                    <EditBucketModal
                        show={showEditModal}
                        bucket={bucket}
                        onUpdateBucket={onUpdateBucket}
                        onClose={this.onModalClose}
                    />
                )}

                {isAdmin && (
                    <BucketOptionsModal
                        show={showOptionsModal}
                        bucket={bucket}
                        onEditBucket={this.onEditBucket}
                        onDeleteBucket={onDeleteBucket}
                        onClose={this.onModalClose}
                    />
                )}
            </Paper>
        );
    }
}

Bucket.propTypes = {
    bucket: PropTypes.object.isRequired,
    onBucketAvatarChange: PropTypes.func.isRequired,
    onDeleteBucket: PropTypes.func.isRequired,
    isAdmin: PropTypes.bool,
};

Bucket.defaultProps = {
    isAdmin: false,
};

const style = ({ palette, spacing, bucket }) => ({
    avatar: {
        width: '46px',
        height: '46px',
        borderRadius: '4px',
        marginRight: spacing.unit,
    },
    bucketLink: {
        width: '100%',
        height: '100%',
        lineHeight: '66px',
        marginLeft: spacing.unit,
        paddingRight: spacing.unit * 4,
        color: 'black',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
    descriptionLink: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    bucketOptionsLink: {
        position: 'absolute',
        right: 0,
        outline: 'none',
        border: 'none',
        backgroundColor: 'white',
        cursor: 'pointer',
        padding: 0,
    },
    content: {
        display: 'flex',
        flexWrap: 'wrap',
        width: bucket.width,
        height: '250px',
        padding: [spacing.unit, spacing.unit * 2],
        borderRadius: '4px',
        borderBottom: `1px solid ${palette.separator.main}`,
        margin: spacing.unit,
        '&:hover': {
            boxShadow: '0px 3px 5px 0px rgba(0, 0, 0, 0.4)',
        },
    },
    title: {
        display: 'flex',
        position: 'relative',
        borderBottom: `1px solid ${palette.separator.main}`,
        width: '100%',
        height: '48px',
        fontWeight: '600',
        alignItems: 'center',
        boxShadow: 'none',
    },
    description: {
        overflow: 'hidden',
        color: palette.text.gray,
        width: '100%',
        height: '155px',
        '& p': {
            marginTop: '0px',
        },
    },
    icon: {
        composes: ['material-icons'],
        color: palette.primary.main,
        marginRight: spacing.unit * 2,
    },
    editIcon: {
        composes: ['material-icons'],
        color: palette.primary.main,
        marginLeft: 'auto',
    },
});

const mapStateToProps = (state, ownProps) => {
    const bucket = ownProps.bucket || {};

    return {
        picture: bucket.pictureLargeURL,
        title: bucket.name,
        isAdmin: bucketSelectors.getIsCurrentUserAdminForCurrentBucket(
            state,
            bucket,
        ),
    };
};

const mapDispatchToProps = dispatch => ({
    onBucketAvatarChange: (avatar, bucketId) => {
        dispatch({
            type: bucketsConstants.UPDATE_BUCKET_AVATAR_REQUEST,
            payload: {
                avatar,
                bucketId,
            },
        });
    },
    onDeleteBucket: bucketId => {
        dispatch({
            type: organizationConstants.DELETE_BUCKET_REQUEST,
            payload: {
                bucketId,
            },
        });
    },
    onUpdateBucket: bucket => {
        dispatch({
            type: bucketsConstants.UPDATE_BUCKET_REQUEST,
            payload: {
                bucket,
            },
        });
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(injectSheet(style)(Bucket));



// WEBPACK FOOTER //
// ./src/components/bucket/Bucket.js